import {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  Fragment,
  useMemo,
} from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Form,
  Container,
  Modal,
  CardGroup,
  Alert,
  ListGroup,
} from 'react-bootstrap';
import { useHotkeys } from 'react-hotkeys-hook';
import { useInView } from 'react-intersection-observer';
import {
  useEvalConfig,
  SessionSubscriptionManager,
  useJudgingEnabled,
  useSessionByKey,
  useTeamsByAthleteId,
  useCombinedSessions,
  useCombinedTeamsByAthleteIds,
} from './hooks';
import { checkIcon } from '../helpers/icons';
import { useQuery, NetworkStatus } from '@apollo/client';
import GetFullSession from '../../apollo/queries/GetFullSession.graphql';
import { useQueryParams } from '../../utilities/query';
import {
  // useScoreSheet,
  useCombineScoreSheet,
  consolidateIndividualLineups,
  prettyTeamScoring,
  scoresheetFormat,
  isTwoJudgePanel,
} from '../../utilities/scoring';
import officialscoresheetStyles from './officialscoresheet.module.css';
import { abbvToApparatusName, GYMapparatus } from '../../utilities/conversions';
import {
  GenderType,
  GymApparatus,
  ScoreType,
  SessionTeamScoreType,
} from '../../models';
import { alertActions } from '../../redux/_actions';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { debounce } from 'lodash';
import Header from '../helpers/header';
import MenuBubble from '../helpers/menububble';
import UserBubble from '../helpers/userbubble';
import { useQueryParamsAdv } from '../../utilities/query';
import { UpdateSession } from '../../apollo/mutations/UpdateSession.graphql';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FullscreenLoading from '../helpers/fullscreenloading';
// import { hide } from '@popperjs/core';
import {
  ScrollShadow,
  ScoreSheetSetupButton,
  ScoreSheetPrintButton,
  ScoreSheetPrevButton,
  ScoreSheetNextButton,
} from './scoresheet/shared/controls';
import { Footer } from './scoresheet/shared/footer';
import { ScoreSheetContext } from './scoresheet/shared/context';
import { exportPDF } from '../../utilities/exports';
import { MensH2HScoresheet } from './scoresheet/mens-h2h/MensH2HScoresheet';
import { WomensSingleScoresheet } from './scoresheet/womens-single/WomensSingleScoresheet';
import {
  TeamSummaryScoreSheet as TeamSummarySheet,
  AAScoreSheet as AASheet,
  ApparatusScoreSheet as ApparatusSheet,
  TeamBreakdownScoreSheet as BreakdownSheet,
  RotationSummaryScoreSheet as RotationSheet,
  LineupVerificationScoreSheet as LineupSheet,
  ChampionshipsScoreSheet as ChampionshipsSheet,
} from './scoresheet/sheets';

const sanitizeAttendanceValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }

  return String(value).replace(/[^\d]/g, '');
};

const formatAttendanceValue = (value) => {
  const sanitizedValue = sanitizeAttendanceValue(value);
  return sanitizedValue ? Number(sanitizedValue).toLocaleString() : '';
};

// Imports for helpers and exported functions moved to utilities – old definitions removed

const PdfOptions = {
  ALL: 'ALL',
  CURRENT: 'CURRENT',
  OFFICIAL: 'OFFICIAL',
  RANKINGS: 'RANKINGS',
  CUSTOM: 'CUSTOM',
};

// const SCORESHEET_TYPES = {
//   TEAM: 'TEAM',
//   SUMMARY: 'SUMMARY',
//   CHAMPIONSHIPS: 'CHAMPIONSHIPS',
//   APPARATUS: 'APPARATUS',
//   AA: 'AA',
//   BREAKDOWN: 'BREAKDOWN',
// };

function ScoreSheetConfigContext(props) {
  const {
    format,
    session,
    noJudge,
    lineupVerificationMode,
    rotationSummaryMode,
    singleSheet,
    officialScoreSheetOnlyMode,
    singleTeamMode,
    h2hMode,
    sideBySideMode,
    panelType,
  } = props;

  // Check if head-to-head layout is supported for this panel type
  const supportsH2h = isTwoJudgePanel(panelType);

  // Check if this is a men's panel (NCAAM or NCAAM4)
  const isMensPanel = panelType?.startsWith('NCAAM');

  // Default to single team mode for women's, head-to-head for men's
  // URL params can override: singleteam=1 forces single team, h2h=1 forces head-to-head
  const getInitialSingleTeamMode = () => {
    // If singleteam=1 URL param is set, use single team mode
    if (singleTeamMode) return true;
    // If h2h=1 URL param is set and panel supports it, use head-to-head (not single team)
    if (h2hMode && supportsH2h) return false;
    // Men's defaults to head-to-head (not single team)
    if (isMensPanel) return false;
    // Women's and others default to single team mode
    return true;
  };

  const [multiDualSheets, setMultiDualSheets] = useState(
    !!format?.isMakeMultiDuals
  );
  const [lineupVerification, setLineupVerification] = useState(
    lineupVerificationMode
  );
  const [rotationSummary, setRotationSummary] = useState(rotationSummaryMode);
  const [officialScoreSheetOnly, setOfficialScoreSheetOnly] = useState(
    officialScoreSheetOnlyMode
  );
  const [showExhibitions, setShowExhibitions] = useState(false);
  const [combineSessions, setCombineSessions] = useState(false);
  const [singleSheetOnly, setSingleSheetOnly] = useState(singleSheet);
  const [championshipsStyle, setChampionshipsStyle] = useState(
    !!format?.isChampionships
  );
  const [singleTeamOfficial, setSingleTeamOfficial] = useState(
    getInitialSingleTeamMode
  );
  const [hideJudgeNames, setHideJudgeNames] = useState(noJudge);
  const [sideBySide, setSideBySide] = useState(!!sideBySideMode);
  const [expandIndividuals, setExpandIndividuals] = useState(false);
  const [pdfOption, setPdfOption] = useState(PdfOptions.ALL);
  const teamScoring = session.teamScoring;

  const toggleLineupVerification = () =>
    setLineupVerification((prev) => {
      const nextValue = !prev;
      if (nextValue) {
        setRotationSummary(false);
        setMultiDualSheets(false);
      }
      return nextValue;
    });
  const toggleRotationSummary = () =>
    setRotationSummary((prev) => {
      const nextValue = !prev;
      if (nextValue) {
        setLineupVerification(false);
        setMultiDualSheets(false);
      }
      return nextValue;
    });
  const toggleMultiDualSheets = () =>
    setMultiDualSheets((prev) => {
      const nextValue = !prev;
      if (nextValue) {
        setLineupVerification(false);
        setRotationSummary(false);
      }
      return nextValue;
    });
  const toggleShowExhibitions = () => setShowExhibitions(!showExhibitions);
  const toggleCombineSessions = () => setCombineSessions(!combineSessions);
  const toggleSingleSheetOnly = () => {
    setSingleSheetOnly((prev) => {
      if (!prev) setSideBySide(false); // Disable side-by-side when enabling single sheet
      return !prev;
    });
  };
  const toggleHideJudgeNames = () => setHideJudgeNames(!hideJudgeNames);
  const toggleExpandIndividuals = () =>
    setExpandIndividuals(!expandIndividuals);
  const toggleOfficialScoreSheetOnly = () =>
    setOfficialScoreSheetOnly(!officialScoreSheetOnly);
  const toggleChampionshipsStyle = () =>
    setChampionshipsStyle(!championshipsStyle);
  const toggleSingleTeamOfficial = () => setSingleTeamOfficial((prev) => !prev);
  const toggleSideBySide = () => {
    setSideBySide((prev) => {
      if (!prev) setSingleSheetOnly(false); // Disable single sheet when enabling side-by-side
      return !prev;
    });
  };

  const config = {
    multiDualSheets,
    lineupVerification,
    rotationSummary,
    showExhibitions,
    combineSessions,
    singleSheetOnly,
    officialScoreSheetOnly,
    hideJudgeNames,
    championshipsStyle,
    singleTeamOfficial,
    sideBySide,
    supportsH2h,
    expandIndividuals,
    pdfOption,
    toggleMultiDualSheets,
    toggleLineupVerification,
    toggleRotationSummary,
    toggleShowExhibitions,
    toggleCombineSessions,
    toggleSingleSheetOnly,
    toggleOfficialScoreSheetOnly,
    toggleHideJudgeNames,
    toggleChampionshipsStyle,
    toggleSingleTeamOfficial,
    toggleSideBySide,
    toggleExpandIndividuals,
    setPdfOption,
    teamScoring,
  };

  return (
    <ScoreSheetContext.Provider value={config}>
      {props.children}
    </ScoreSheetContext.Provider>
  );
}

// function CombineSessionContext(props) {}

function ScoreSheetConfig(props) {
  const {
    teams,
    setTeamAIndex,
    setTeamBIndex,
    teamAIndex,
    teamBIndex,
    location,
    attendance,
    setLocation,
    setAttendance,
    sessionId,
    sessionVersion,
    setShow,
    setAltTeamScoring,
    altTeamScoring,
    setCombineKey,
    setCombineSessions,
    // combineSessions,
    combineSession,
    combineKey,
    combineLoaded,
    combinedSessionsLoaded,
    setCombinedSessionsLoaded,
    combineKeys,
    setCombineKeys,
    combinedSessions,
  } = props;

  const [combineURL, setCombineURL] = useState('');
  const { scoreSheetSetup, row, customControl } = officialscoresheetStyles;
  const [updateSession] = useMutation(UpdateSession);
  const debouncedUpdate = useCallback(debounce(updateSessionDetails, 1000), [
    sessionVersion,
  ]);
  const dispatch = useDispatch();
  const judgingEnabled = useJudgingEnabled();
  const config = useContext(ScoreSheetContext);
  const { alert } = useSelector((state) => state);

  const {
    multiDualSheets,
    lineupVerification,
    rotationSummary,
    // showExhibitions,
    // combineSessions,
    singleSheetOnly,
    officialScoreSheetOnly,
    hideJudgeNames,
    singleTeamOfficial,
    sideBySide,
    supportsH2h,
    expandIndividuals,
    // pdfOption,
    toggleMultiDualSheets,
    toggleLineupVerification,
    toggleRotationSummary,
    // toggleShowExhibitions,
    // toggleCombineSessions,
    toggleSingleSheetOnly,
    toggleOfficialScoreSheetOnly,
    toggleHideJudgeNames,
    toggleSingleTeamOfficial,
    toggleSideBySide,
    toggleExpandIndividuals,
    // setPdfOption,
    teamScoring,
  } = config;

  const validationSchema = Yup.object().shape({
    multiDualSheets: Yup.boolean(),
    lineupVerification: Yup.boolean(),
    rotationSummary: Yup.boolean(),
    showExhibitions: Yup.boolean(),
    combineSessions: Yup.boolean(),
    singleSheetOnly: Yup.boolean(),
    officialScoreSheetOnly: Yup.boolean(),
    hideJudgeNames: Yup.boolean(),
    singleTeamOfficial: Yup.boolean(),
    pdfOption: Yup.string(),
  });

  const getDefault = (original) => {
    // console.log(original);
    const result = {
      multiDualSheets: !!original?.multiDualSheets,
      lineupVerification: !!original?.lineupVerification,
      rotationSummary: !!original?.rotationSummary,
      showExhibitions: !!original?.showExhibitions,
      combineSessions: !!original?.combineSessions,
      singleSheetOnly: !!original?.singleSheetOnly,
      officialScoreSheetOnly: !!original?.officialScoreSheetOnly,
      hideJudgeNames: !!original?.hidgeJudgeNames,
      singleTeamOfficial: !!original?.singleTeamOfficial,
      pdfOption: original?.pdfOption || PdfOptions.ALL,
      teamScoring: teamScoring,
    };
    return result;
  };

  const { setFieldValue, /* handleSubmit, handleChange, */ values } = useFormik(
    {
      initialValues: getDefault(config),
      validationSchema,
      validateOnChange: false,
      validateOnBlur: false,
      onSubmit(values) {},
    }
  );

  function updateSessionDetails({ newLocation, newAttendance }) {
    const input = {
      id: sessionId,
      _version: sessionVersion,
    };

    if (newLocation) {
      input['location'] = newLocation;
    }

    if (newAttendance) {
      input['attendance'] = newAttendance;
    }

    return updateSession({ variables: { input } })
      .then(() => {
        if (newLocation) {
          dispatch(alertActions.notification('Session location updated.'));
        }
        if (newAttendance) {
          dispatch(alertActions.notification('Session attendance updated.'));
        }
      })
      .catch(() => {
        dispatch(
          alertActions.error('Unable to update session details. Please retry.')
        );
      });
  }

  function handleSetLocation(e) {
    setLocation(e.target.value);
    debouncedUpdate({ newLocation: e.target.value, newAttendance: null });
  }

  function handleSetAttendance(e) {
    const sanitizedValue = sanitizeAttendanceValue(e.target.value);
    setAttendance(sanitizedValue);
    debouncedUpdate({ newAttendance: sanitizedValue, newLocation: null });
  }

  function handleAddCombineSession(e) {
    e.preventDefault();

    const sessionKey = combineURL.split('https://virti.us/session?s=')?.[1];

    if (!sessionKey || sessionKey.length !== 10) {
      dispatch(alertActions.error('Invalid combine session URL.'));
      return;
    }

    // Check if this session is already added
    if (combineKeys.includes(sessionKey)) {
      dispatch(alertActions.error('This session is already added.'));
      return;
    }

    // Check if we've reached the maximum of 3 additional sessions
    const additionalSessionCount = (combineKey ? 1 : 0) + combineKeys.length;
    if (additionalSessionCount >= 3) {
      dispatch(
        alertActions.error(
          'Maximum of 3 additional sessions reached. Please remove a session before adding another.'
        )
      );
      return;
    }

    // Show loading alert
    dispatch(
      alertActions.notification(`Loading combine session: ${combineURL}`, false)
    );

    // Add to the array of keys
    setCombineKeys([...combineKeys, sessionKey]);
    setCombineURL(''); // Clear the input field
  }

  function handleRemoveCombineSession(index) {
    // Store the key being removed
    const removedKey = combineKeys[index];

    // Create a new array without the removed session
    const newCombineKeys = [...combineKeys];
    newCombineKeys.splice(index, 1);

    // Update the keys state
    setCombineKeys(newCombineKeys);

    // Also remove from the loaded tracking object
    const newCombinedSessionsLoaded = { ...combinedSessionsLoaded };
    delete newCombinedSessionsLoaded[removedKey];
    setCombinedSessionsLoaded(newCombinedSessionsLoaded);

    dispatch(alertActions.notification('Combine session removed.'));
  }

  function handleResetCombineSessions() {
    setCombineKeys([]);
    setCombineSessions([]);
    setCombineURL('');
    setCombineKey('');
    // Clear the loaded sessions tracking
    setCombinedSessionsLoaded({});
    dispatch(alertActions.notification('All combine sessions reset.'));
  }

  // For backward compatibility
  // function handleLoadCombineSession(e) {
  //   e.preventDefault();

  //   const sessionKey = combineURL.split('https://virti.us/session?s=')?.[1];

  //   if (!sessionKey || sessionKey.length !== 10) {
  //     dispatch(alertActions.error('Invalid combine session URL.'));
  //     return;
  //   }

  //   // Check if we've reached the maximum of 3 additional sessions
  //   const additionalSessionCount = combineKeys.length;
  //   if (additionalSessionCount >= 3) {
  //     dispatch(alertActions.error('Maximum of 3 additional sessions reached. Please remove a session before adding another.'));
  //     return;
  //   }

  //   dispatch(alertActions.notification(`Loading combine session: ${combineURL}`, false));
  //   setCombineKey(sessionKey);
  // }

  function handleResetCombineSession() {
    // Get the key being removed
    const keyBeingRemoved = combineKey;
    setCombineKey('');
    setCombineURL('');
    setCombineSessions([]);

    // Update the loaded state to reflect the removal
    if (keyBeingRemoved) {
      const newCombinedSessionsLoaded = { ...combinedSessionsLoaded };
      delete newCombinedSessionsLoaded[keyBeingRemoved];
      setCombinedSessionsLoaded(newCombinedSessionsLoaded);
    }

    dispatch(alertActions.notification('Combine session reset.'));
  }

  // console.log(combinedSessions, combinedSessionsLoaded, combineKey, combineKeys);

  const setupBody = () => {
    return (
      <Container className={scoreSheetSetup}>
        {!alert.clear ? (
          <Row style={{ margin: '0 0 1rem 0' }}>
            <Alert
              dismissible
              onClose={() => dispatch(alertActions.clear())}
              variant={alert.type === 'alert-danger' ? 'danger' : 'success'}
              style={{ textAlign: 'center', width: '100%' }}
            >
              {alert.message}
            </Alert>
          </Row>
        ) : null}
        <CardGroup>
          <Card style={{ width: '33%' }}>
            <Card.Header>Header</Card.Header>
            <Card.Body>
              <Row className={row}>
                <Col>
                  <Card.Subtitle>Competition Details (Required)</Card.Subtitle>
                  <Row>
                    <Col>
                      <label>Location:</label>
                      <Form.Control
                        plaintext
                        name="location"
                        type="text"
                        autoComplete="off"
                        value={location}
                        onChange={handleSetLocation}
                        placeholder="Enter location..."
                        disabled={!judgingEnabled}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <label>Attendance:</label>
                      <Form.Control
                        plaintext
                        name="attendance"
                        type="text"
                        autoComplete="off"
                        value={formatAttendanceValue(attendance)}
                        onChange={handleSetAttendance}
                        placeholder="Enter attendance..."
                        disabled={!judgingEnabled}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className={row}>
                <Col>
                  <Card.Subtitle>
                    Custom Team Selection (Optional)
                  </Card.Subtitle>
                  <Row>
                    <Col>
                      <label>Home:</label>
                      <Form.Control
                        as="select"
                        value={teamAIndex ?? 'Select...'}
                        disabled={!singleSheetOnly}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onChange={(e) => {
                          setTeamAIndex(e.target.value);
                        }}
                      >
                        <option value="Select...">Select...</option>
                        {teams.map((t, i) => {
                          return (
                            <option key={`${t?.name}_home_${i}`} value={i}>
                              {t?.name}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <label>Visitor:</label>
                      <Form.Control
                        as="select"
                        value={teamBIndex ?? 'Select...'}
                        disabled={!singleSheetOnly}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onChange={(e) => {
                          setTeamBIndex(e.target.value);
                        }}
                      >
                        <option value="Select...">Select...</option>
                        {teams.map((t, i) => {
                          return (
                            <option key={`${t?.name}_visitor_${i}`} value={i}>
                              {t?.name}
                            </option>
                          );
                        })}
                      </Form.Control>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          <Card style={{ width: '33%' }}>
            <Card.Header>Settings</Card.Header>
            <Card.Body>
              <Row className={row}>
                <Col>
                  <Row>
                    <Col>
                      <Card.Subtitle>Forms</Card.Subtitle>
                      {supportsH2h && (
                        <Form.Check
                          type="checkbox"
                          label="Head-to-Head Layout"
                          custom
                          id="checkHeadToHead"
                          className={customControl}
                          checked={!singleTeamOfficial}
                          onChange={toggleSingleTeamOfficial}
                        />
                      )}
                      <Form.Check
                        type="checkbox"
                        label="Lineup Verification"
                        custom
                        id="checkLineupVerification"
                        className={customControl}
                        checked={lineupVerification}
                        onChange={toggleLineupVerification}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Rotation Summary"
                        custom
                        id="checkRotationSummary"
                        className={customControl}
                        checked={rotationSummary}
                        onChange={toggleRotationSummary}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Multi Dual Scoresheets"
                        custom
                        disabled={teams.length < 3}
                        id="checkMultiDual"
                        className={customControl}
                        checked={multiDualSheets}
                        onChange={toggleMultiDualSheets}
                      />
                      {/*<Form.Check
                        type="checkbox"
                        label="Combine Sessions"
                        custom
                        id="checkCombine"
                        className={customControl}
                        onChange={(e) => {
                          setFieldValue(
                            'combineSessions',
                            !values.combineSessions
                          );
                        }}
                      />*/}
                    </Col>
                  </Row>
                </Col>
                <Col>
                  <Row>
                    <Col>
                      <Card.Subtitle>Display</Card.Subtitle>
                      <Form.Check
                        type="checkbox"
                        label="Official Score Sheet Only"
                        custom
                        id="checkOfficialSheet"
                        className={customControl}
                        checked={officialScoreSheetOnly}
                        onChange={toggleOfficialScoreSheetOnly}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Single Sheet Only"
                        custom
                        id="checkSingle"
                        className={customControl}
                        checked={singleSheetOnly}
                        onChange={toggleSingleSheetOnly}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Hide Judge Names"
                        custom
                        id="checkJudge"
                        className={customControl}
                        checked={hideJudgeNames}
                        onChange={toggleHideJudgeNames}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Side-by-Side View"
                        custom
                        id="checkSideBySide"
                        className={customControl}
                        checked={sideBySide}
                        onChange={toggleSideBySide}
                      />
                      <Form.Check
                        type="checkbox"
                        label="Expand Individual Lineups"
                        custom
                        id="checkExpandIndividuals"
                        className={customControl}
                        checked={expandIndividuals}
                        onChange={toggleExpandIndividuals}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className={row}>
                <Col>
                  <Row>
                    <Col>
                      <Card.Subtitle>PDF</Card.Subtitle>
                      <div key="pdfRadio">
                        <Form>
                          <fieldset>
                            <Form.Check
                              type="radio"
                              label="All Pages"
                              custom
                              name="pdfGroup"
                              id="pdfRadio-1"
                              className={customControl}
                              checked={values.pdfOption === PdfOptions.ALL}
                              onChange={(e) => {
                                setFieldValue('pdfOption', PdfOptions.ALL);
                              }}
                            />
                            {/*<Form.Check
                              type="radio"
                              label="Current Page Only"
                              custom
                              name="pdfGroup"
                              id="pdfRadio-2"
                              className={customControl}
                              checked={values.pdfOption === PdfOptions.CURRENT}
                              onChange={(e) => {
                                setFieldValue('pdfOption', PdfOptions.CURRENT);
                              }}
                            />
                            <Form.Check
                              type="radio"
                              label="Official Scoresheets Only"
                              custom
                              name="pdfGroup"
                              id="pdfRadio-3"
                              className={customControl}
                              checked={values.pdfOption === PdfOptions.OFFICIAL}
                              onChange={(e) => {
                                setFieldValue('pdfOption', PdfOptions.OFFICIAL);
                              }}
                            />
                            <Form.Check
                              type="radio"
                              label="Rankings/Results"
                              custom
                              name="pdfGroup"
                              id="pdfRadio-4"
                              className={customControl}
                              checked={values.pdfOption === PdfOptions.RANKINGS}
                              onChange={(e) => {
                                setFieldValue('pdfOption', PdfOptions.RANKINGS);
                              }}
                            />
                            <Form.Check
                              type="radio"
                              label="Custom"
                              custom
                              name="pdfGroup"
                              id="pdfRadio-5"
                              className={customControl}
                              checked={values.pdfOption === PdfOptions.CUSTOM}
                              onChange={(e) => {
                                setFieldValue('pdfOption', PdfOptions.CUSTOM);
                              }}
                            />
                            */}
                          </fieldset>
                        </Form>
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <Row className={row}>
                <Col>
                  <Card.Subtitle>Team Score (Ties)</Card.Subtitle>
                  <div key="teamScoreAlgos">
                    <label>Options:</label>
                    <Form.Control
                      as="select"
                      value={
                        altTeamScoring
                          ? prettyTeamScoring(altTeamScoring)
                          : `${prettyTeamScoring(values.teamScoring)} (Default)`
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                      onChange={(e) => {
                        const selectedMethod =
                          SessionTeamScoreType[
                            Object.keys(SessionTeamScoreType)[
                              e.target.selectedIndex - 1
                            ]
                          ];

                        const newValue =
                          values.teamScoring === selectedMethod
                            ? null
                            : selectedMethod;

                        setAltTeamScoring(newValue);
                      }}
                    >
                      <option value="Select..." hidden disabled>
                        Select...
                      </option>
                      {Object.values(SessionTeamScoreType).map((type, i) => {
                        return (
                          <option key={i}>
                            {prettyTeamScoring(type)}
                            {values.teamScoring === type ? ' (Default)' : ''}
                          </option>
                        );
                      })}
                    </Form.Control>
                  </div>
                </Col>
              </Row>
              <Row className={row}>
                <Col>
                  <Card.Subtitle>
                    Combine Sessions{' '}
                    {(Object.keys(combinedSessionsLoaded).length > 0 ||
                      (combineKey && combineLoaded)) &&
                    (combineKeys.length > 0 || combineKey) ? (
                      <strong>
                        (LOADED:{' '}
                        {Object.keys(combinedSessionsLoaded).length +
                          (combineKey && combineLoaded ? 1 : 0)}
                        ) <span className="green">{checkIcon}</span>
                      </strong>
                    ) : null}
                  </Card.Subtitle>
                  <div>
                    <label>Add Session:</label>
                    <Form>
                      <Form.Control
                        plaintext
                        name="combineSession"
                        type="text"
                        autoComplete="off"
                        value={combineURL || ''}
                        onChange={(e) => setCombineURL(e.target.value)}
                        placeholder="Enter session URL..."
                        onKeyPress={(event) => {
                          if (event.key === 'Enter') {
                            handleAddCombineSession(event);
                          }
                        }}
                      />
                      <Row>
                        <Col style={{ padding: '1rem' }}>
                          <Button
                            variant="light"
                            style={{ width: '100%' }}
                            onClick={handleResetCombineSessions}
                          >
                            Reset All
                          </Button>
                        </Col>
                        <Col style={{ padding: '1rem' }}>
                          <Button
                            variant="outline-secondary"
                            style={{ width: '100%' }}
                            onClick={handleAddCombineSession}
                          >
                            Add
                          </Button>
                        </Col>
                      </Row>
                    </Form>

                    {/* Display list of combined sessions */}
                    {(combineKeys.length > 0 ||
                      (combineKey && combineLoaded)) && (
                      <div style={{ marginTop: '1rem' }}>
                        <label>
                          Combined Sessions:{' '}
                          <span className="text-muted">(Max 3 total)</span>
                        </label>
                        <ListGroup>
                          {combineKey && (
                            <ListGroup.Item className="d-flex justify-content-between align-items-center">
                              <div>
                                {!combineLoaded ? (
                                  <span className="text-muted">
                                    Loading session...
                                  </span>
                                ) : combineSession?.session ? (
                                  <>
                                    <div>
                                      {combineSession.session.name ||
                                        'Session 1'}
                                    </div>
                                    <small className="text-muted">
                                      <a
                                        href={`https://virti.us/session?s=${combineKey}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        https://virti.us/session?s={combineKey}
                                      </a>
                                    </small>
                                  </>
                                ) : (
                                  'Loading failed'
                                )}
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={handleResetCombineSession}
                              >
                                Remove
                              </Button>
                            </ListGroup.Item>
                          )}
                          {combineKeys.map((key, index) => (
                            <ListGroup.Item
                              key={index}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <div>
                                {!combinedSessions[index] ? (
                                  <span className="text-muted">
                                    Loading session...
                                  </span>
                                ) : (
                                  <>
                                    <div>
                                      {combinedSessions[index]?.name ||
                                        `Session ${
                                          index + 1 + (combineKey ? 1 : 0)
                                        }`}
                                    </div>
                                    <small className="text-muted">
                                      <a
                                        href={`https://virti.us/session?s=${key}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        https://virti.us/session?s={key}
                                      </a>
                                    </small>
                                  </>
                                )}
                              </div>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() =>
                                  handleRemoveCombineSession(index)
                                }
                              >
                                Remove
                              </Button>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                        <div
                          className="text-muted mt-2"
                          style={{ fontSize: '0.8rem' }}
                        >
                          {(combineKey ? 1 : 0) + combineKeys.length} of 3
                          additional sessions used
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </CardGroup>
      </Container>
    );
  };

  const footer = () => {
    return (
      <Row className="vCenter" style={{ margin: '0 auto' }}>
        <Button
          variant="outline-secondary"
          className="formButton"
          onClick={() => setShow(false)}
        >
          Close
        </Button>
      </Row>
    );
  };

  const headerMsg = () => {
    return <span>Scoresheet Setup</span>;
  };
  return (
    <>
      <Modal.Header className="setupHeader">{headerMsg()}</Modal.Header>
      <Modal.Body className="setupForm">{setupBody()}</Modal.Body>
      <Modal.Footer className="setupFooter">{footer()}</Modal.Footer>
    </>
  );
}

function ScoreSheetSetup(props) {
  const { show, setShow } = props;

  return (
    <Modal
      show={show}
      onHide={() => setShow(false)}
      centered
      dialogClassName="setupModal"
      animation={false}
      size="xl"
    >
      <ScoreSheetConfig {...props} />
    </Modal>
  );
}

function generateSheetOptions(
  teams,
  individuals,
  format,
  apparatus = [],
  options = {}
) {
  const { singleTeamOfficial } = options;
  const isChampionships = !!format?.isChampionships;
  const isMakeMultiDuals = !!format?.isMakeMultiDuals;
  const isLineupVerification = !!format?.isLineupVerification;
  const isRotationSummary = !!format?.isRotationSummary;
  const isOfficialScoreSheetOnly = !!format?.isOfficialScoreSheetOnly;
  const numTeams = teams.length || 0;
  const numAA = individuals?.AA?.length || 0;

  const summary = numTeams > 0 ? ['SUMMARY'] : [];
  const AA = numTeams > 0 || numAA > 0 ? ['AA'] : [];
  const championships = isChampionships
    ? teams.map((t, i) => `CHAMPIONSHIPS${i}`)
    : [];
  const singleTeamSheets = singleTeamOfficial
    ? teams.map((t, i) => `SINGLE_TEAM${i}`)
    : [];

  const teamPairs = [];
  const breakdown = [];
  const lineups = [];

  for (let i = 0; i < Math.round(numTeams / 2); i++) {
    breakdown.push(`BREAKDOWN${i * 2}_${i * 2 + 1}`);
    if (!isMakeMultiDuals) {
      teamPairs.push(`TEAM${i * 2}_${i * 2 + 1}`);
    }
  }

  for (let i = 0; i < Math.round(numTeams); i++) {
    lineups.push(`LINEUP${i}`);
  }

  if (isMakeMultiDuals) {
    for (let i = 0; i < numTeams; i++) {
      for (let j = i + 1; j < numTeams; j++) {
        const pair = `TEAM${i}_${j}`;
        teamPairs.push(pair);
      }
    }
  }

  if (isRotationSummary) {
    return ['ROTATION_SUMMARY'];
  }

  if (isLineupVerification) {
    return [...lineups];
  }

  const baseSheets = singleTeamOfficial
    ? singleTeamSheets
    : isChampionships
    ? championships
    : teamPairs;

  if (isOfficialScoreSheetOnly) {
    return [...baseSheets];
  }

  return [...baseSheets, ...summary, ...breakdown, ...apparatus, ...AA];
}

const SCORESHEET_TYPES = {
  TEAM: 'TEAM',
  SUMMARY: 'SUMMARY',
  CHAMPIONSHIPS: 'CHAMPIONSHIPS',
  SINGLE_TEAM: 'SINGLE_TEAM',
  APPARATUS: 'APPARATUS',
  AA: 'AA',
  BREAKDOWN: 'BREAKDOWN',
  LINEUP: 'LINEUP',
  ROTATION_SUMMARY: 'ROTATION_SUMMARY',
};

export default function OfficialScoreSheet(props) {
  const [requestedCopy, setRequestedCopy] = useState(false);
  const { sessionId } = props;
  const judgingEnabled = useJudgingEnabled();

  // Check if this is a PDF generation request (has pdfToken)
  const urlParams = useQueryParams();
  const isPdfMode = !!urlParams.get('pdfToken');

  // Check for Lambda-injected session data (for PDF generation)
  // This data is injected by Puppeteer before the page loads
  const injectedSessionData = useMemo(() => {
    if (typeof window !== 'undefined' && window.__INJECTED_SESSION_DATA__) {
      return window.__INJECTED_SESSION_DATA__;
    }
    return null;
  }, []);

  // Get the full query params to preserve them on redirect
  const { queryParams } = useQueryParamsAdv(
    {
      s: sessionId,
      officialscoresheet: 0,
      combinekey: '',
      tz: '',
      nojudge: '',
      print: '',
      lineupverification: '',
      rotationsummary: '',
      singlesheet: '',
      officialonly: '',
      singleteam: '',
      h2h: '',
      sidebyside: '',
    },
    true
  );

  const shouldRedirectToSession = !judgingEnabled && !isPdfMode;

  useEffect(() => {
    if (shouldRedirectToSession) {
      const sessionKey = urlParams.get('s');
      window.location.replace(`/session?s=${sessionKey}`);
    }
  }, [shouldRedirectToSession, urlParams]);

  // const { role } = useSelector((state) => state.session);

  // Skip GraphQL query if we have injected data from Lambda
  // Check window directly (not via useMemo) to ensure it's available during first render
  const hasInjectedData =
    typeof window !== 'undefined' && !!window.__INJECTED_SESSION_DATA__;

  // Otherwise use full query for authenticated users
  const { data, networkStatus, loading, refetch } = useQuery(GetFullSession, {
    variables: { id: sessionId },
    skip: hasInjectedData, // Skip query if Lambda provided data
  });

  useEffect(() => {
    if (data && requestedCopy && networkStatus !== NetworkStatus.refetch) {
      setRequestedCopy(false);
    }
  }, [requestedCopy, networkStatus, data]);

  // Use injected data from Lambda if available, otherwise use GraphQL data
  const session = injectedSessionData || data?.getSession || {};
  // When using injected data, we're not loading (data is already available)
  const isLoading = injectedSessionData ? false : loading;

  if (shouldRedirectToSession) {
    return null;
  }

  const format = scoresheetFormat(session?.judgePanel);
  const newProps = {
    ...props,
    session,
    loading: isLoading,
    isPdfMode,
    refetch,
  };

  if (isLoading && !session?.id) {
    return <FullscreenLoading message="Loading Scoresheet..." />;
  }

  return (
    <ScoreSheetConfigContext
      session={session}
      format={format}
      noJudge={queryParams?.nojudge} // Judge names now available in PDF mode via IAM-authenticated data injection
      singleSheet={queryParams?.singlesheet}
      lineupVerificationMode={queryParams?.lineupverification}
      rotationSummaryMode={queryParams?.rotationsummary}
      officialScoreSheetOnlyMode={queryParams?.officialonly}
      singleTeamMode={queryParams?.singleteam}
      h2hMode={queryParams?.h2h}
      sideBySideMode={queryParams?.sidebyside}
      panelType={session?.judgePanel}
    >
      <OfficialScoreSheetInner {...newProps} />
      {/* Disable subscriptions in PDF mode to allow networkidle2 to complete */}
      {!isPdfMode && <SessionSubscriptionManager sessionId={sessionId} />}
    </ScoreSheetConfigContext>
  );
}

function OfficialScoreSheetInner({
  sessionId,
  type,
  session,
  isPdfMode,
  refetch,
}) {
  const evalConfig = useEvalConfig();
  const dispatch = useDispatch();
  const [updateSession] = useMutation(UpdateSession);
  const judgingEnabled = useJudgingEnabled();
  const { queryParams, setQueryParams } = useQueryParamsAdv(
    {
      s: '',
      officialscoresheet: 0,
      combinekey: '',
      combinekeys: '',
      tz: '',
      nojudge: '',
      singlesheet: '',
      lineupverification: '',
      rotationsummary: '',
      print: '',
      officialonly: '',
    },
    true
  ); // using replace instead of push
  const lineupMax = evalConfig?.maxTeamLineup || 6;
  const [teamAIndex, setTeamAIndex] = useState(null);
  const [teamBIndex, setTeamBIndex] = useState(null);
  const [location, setLocation] = useState('');
  const [altTeamScoring, setAltTeamScoring] = useState(null);
  const [attendance, setAttendance] = useState('');
  const [combineSessions, setCombineSessions] = useState([]);
  const [combineKey, setCombineKey] = useState(queryParams?.combinekey || null);
  const [combineKeys, setCombineKeys] = useState(
    queryParams?.combinekeys ? queryParams.combinekeys.split(',') : []
  );
  // const [combineURL, setCombineURL] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  //const [selector, setSelector] = useState('TEAM');
  const [toggleNextSheet, setToggleNextSheet] = useState(true);
  const debouncedToggle = useCallback(debounce(setToggleNextSheet, 1000), []);
  const {
    multiDualSheets,
    lineupVerification,
    rotationSummary,
    hideJudgeNames,
    singleSheetOnly,
    officialScoreSheetOnly,
    singleTeamOfficial,
    sideBySide,
    expandIndividuals,
  } = useContext(ScoreSheetContext);
  // For multiple combine sessions
  const combinedSessions =
    useCombinedSessions({ sessionKeys: combineKeys }) || [];
  const [combinedSessionsLoaded, setCombinedSessionsLoaded] = useState({});

  // Handler for inline location editing (called on blur from child component)
  const handleLocationChange = useCallback(
    (newLocation) => {
      setLocation(newLocation);
      // Call mutation directly (no debounce needed since it's on blur)
      const input = {
        id: sessionId,
        _version: session._version,
        location: newLocation,
      };
      updateSession({ variables: { input } }).catch(() => {
        dispatch(
          alertActions.error('Unable to update session location. Please retry.')
        );
      });
    },
    [sessionId, session._version, updateSession, dispatch]
  );

  // Handler for inline attendance editing (called on blur from child component)
  const handleAttendanceChange = useCallback(
    (newAttendance) => {
      const sanitizedValue = sanitizeAttendanceValue(newAttendance);
      setAttendance(sanitizedValue);
      // Call mutation directly (no debounce needed since it's on blur)
      const input = {
        id: sessionId,
        _version: session._version,
        attendance: sanitizedValue,
      };
      updateSession({ variables: { input } }).catch(() => {
        dispatch(
          alertActions.error(
            'Unable to update session attendance. Please retry.'
          )
        );
      });
    },
    [sessionId, session._version, updateSession, dispatch]
  );

  // For backward compatibility
  const combineSession = useSessionByKey({ sessionKey: combineKey });
  const combineLoaded = !!combineKey && combineSession?.session;

  // Debug logs
  // useEffect(() => {
  //   console.log('combineKeys updated:', combineKeys);
  //   console.log('combinedSessions:', combinedSessions);
  //   console.log('combinedSessionsLoaded:', combinedSessionsLoaded);
  // }, [combineKeys, combinedSessions, combinedSessionsLoaded]);

  // Reset combined sessions loaded state when we're down to 0 or 1 sessions
  useEffect(() => {
    if (combineKeys.length <= 0 && !combineKey) {
      // If no sessions at all, clear everything
      setCombinedSessionsLoaded({});
    }
  }, [combineKeys, combineKey]);

  const timezone =
    queryParams?.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const print = queryParams?.print === '1' ? true : false;
  const noJudge = queryParams?.nojudge === '1' ? true : false;
  const singleSheet = queryParams?.singlesheet === '1' ? true : false;
  const lineupVerificationMode =
    queryParams?.lineupverification === '1' ? true : false;
  const rotationSummaryMode =
    queryParams?.rotationsummary === '1' ? true : false;

  const [sheetIndex, setSheetIndex] = useState(
    parseInt(queryParams?.officialscoresheet) - 1 || 0
  );

  useEffect(() => {
    if (combineLoaded) {
      setCombineSessions(['https://virti.us/session?s=' + combineKey]);

      if (!print) {
        dispatch(
          alertActions.success(
            `Combine session ${
              combineSession?.session?.name || 'Unknown'
            } successfully loaded.`
          )
        );
      }
    }
  }, [combineLoaded, print]);

  useEffect(() => {
    if (
      combinedSessions &&
      combinedSessions.length > 0 &&
      combineKeys.length > 0
    ) {
      const newCombinedSessionsLoaded = {};
      let newSessionsLoaded = false;

      combinedSessions.forEach((session, index) => {
        if (session && !combinedSessionsLoaded[combineKeys[index]]) {
          newCombinedSessionsLoaded[combineKeys[index]] = true;
          newSessionsLoaded = true;

          if (!print) {
            dispatch(
              alertActions.success(
                `Combine session ${session.name} successfully loaded.`
              )
            );
          }
        }
      });

      if (newSessionsLoaded) {
        setCombinedSessionsLoaded({
          ...combinedSessionsLoaded,
          ...newCombinedSessionsLoaded,
        });
      }
    }
  }, [combinedSessions, print, combineKeys, dispatch]);

  useHotkeys(
    's',
    (e) => {
      e.preventDefault();
      setShowSetup(true);
    },
    [showSetup]
  );

  useHotkeys(
    'up',
    (e) => {
      e.preventDefault();
      handleUp(e);
    },
    [sheetIndex]
  );

  useHotkeys(
    'down',
    (e) => {
      e.preventDefault();
      handleDown(e);
    },
    [sheetIndex]
  );

  const handleUp = (e) => {
    e.preventDefault();
    // In side-by-side mode, navigate by pairs (2 at a time)
    const step = sideBySide && !isPdfMode ? 2 : 1;
    const newSheetIndex = sheetIndex >= step ? sheetIndex - step : 0;
    setToggleNextSheet(true);
    setSheetIndex(newSheetIndex);
  };

  const handleDown = (e) => {
    e.preventDefault();
    // In side-by-side mode, navigate by pairs (2 at a time)
    const step = sideBySide && !isPdfMode ? 2 : 1;
    const maxIndex = sheetOptions.length - 1;
    const newSheetIndex =
      sheetIndex + step <= maxIndex ? sheetIndex + step : maxIndex;
    setToggleNextSheet(true);
    setSheetIndex(newSheetIndex);
  };

  // To add the combinekey to the query URL (for backward compatibility)
  useEffect(() => {
    // Skip URL updates in PDF mode to preserve pdfToken
    if (isPdfMode) return;
    setQueryParams({
      ...queryParams,
      combinekey: combineKey || '',
    });
  }, [combineKey, isPdfMode]);

  // To add the combinekeys to the query URL
  useEffect(() => {
    // Skip URL updates in PDF mode to preserve pdfToken
    if (isPdfMode) return;
    setQueryParams({
      ...queryParams,
      combinekeys: combineKeys.length > 0 ? combineKeys.join(',') : undefined,
    });
  }, [combineKeys, isPdfMode]);

  // To add the lineupverification to the query URL
  useEffect(() => {
    // Skip URL updates in PDF mode to preserve pdfToken
    if (isPdfMode) return;

    const newQueryParams = {
      ...queryParams,
      lineupverification: lineupVerification ? 1 : undefined,
      rotationsummary: rotationSummary ? 1 : undefined,
      nojudge: hideJudgeNames ? 1 : undefined,
      singlesheet: singleSheetOnly ? 1 : undefined,
      officialonly: officialScoreSheetOnly ? 1 : undefined,
      // h2h=1 when head-to-head is enabled (singleTeamOfficial is false)
      h2h: !singleTeamOfficial ? 1 : undefined,
      // Remove legacy singleteam param (single team is now the default)
      singleteam: undefined,
      sidebyside: sideBySide ? 1 : undefined,
    };

    if (!lineupVerification) {
      delete newQueryParams.lineupverification;
    }

    if (!rotationSummary) {
      delete newQueryParams.rotationsummary;
    }

    if (!hideJudgeNames) {
      delete newQueryParams.nojudge;
    }

    if (!singleSheetOnly) {
      delete newQueryParams.singlesheet;
    }

    if (!officialScoreSheetOnly) {
      delete newQueryParams.officialonly;
    }

    // Only set h2h param when head-to-head mode is enabled
    if (singleTeamOfficial) {
      delete newQueryParams.h2h;
    }

    // Always remove legacy singleteam param
    delete newQueryParams.singleteam;

    if (!sideBySide) {
      delete newQueryParams.sidebyside;
    }

    setQueryParams(newQueryParams);
  }, [
    lineupVerification,
    rotationSummary,
    hideJudgeNames,
    singleSheetOnly,
    officialScoreSheetOnly,
    singleTeamOfficial,
    sideBySide,
    isPdfMode,
  ]);

  // Sync sheetIndex to URL param
  useEffect(() => {
    if (isPdfMode) return;
    setQueryParams({
      ...queryParams,
      officialscoresheet: sheetIndex + 1, // 1-indexed in URL
    });
  }, [sheetIndex, isPdfMode]);

  // To automatically scroll to new sheetIndex, including on page load
  useEffect(() => {
    const isSafeIndex = sheetIndex > 0 && sheetIndex < sheetOptions.length;
    let safeIndex = 0;

    if (isSafeIndex) {
      safeIndex = sheetIndex;
    } else {
      setSheetIndex(0);
    }

    // console.log(safeIndex);
    // console.log(sheetIndex);
    // console.log(toggleNextSheet);

    // Skip URL updates in PDF mode to preserve pdfToken
    if (!isPdfMode) {
      setQueryParams({
        ...queryParams,
        officialscoresheet: parseInt(safeIndex) + 1,
      });
    }

    if (toggleNextSheet) {
      const element = document.getElementById(`scoresheet_${safeIndex + 1}`);
      if (!element) {
        return;
      }
      element.scrollIntoView({ block: 'start', behavior: 'smooth' });
      debouncedToggle(false);
    }
  }, [sheetIndex, toggleNextSheet, isPdfMode]);

  const gender = session?.gender;
  const sessionKey = session?.sessionKey;
  const sessionName = session?.name;
  // console.log(combinedSessions)

  const scoresheet = useCombineScoreSheet();
  const scoresheetData = scoresheet(session, {
    altTeamScoring,
    combineSession: combineSession?.session,
    combinedSessions: combinedSessions.filter((s) => s),
  });

  // console.log({ scoresheetData, combineScoresheetData });

  const { date, format, sessionData: rawSessionData } = scoresheetData;
  const sessionData = expandIndividuals
    ? rawSessionData
    : consolidateIndividualLineups(rawSessionData);
  const { teams, individuals } = sessionData;
  const rawTeams = rawSessionData?.teams;
  const apparatusArray = Object.keys(GYMapparatus(gender));

  // Get teams by athlete ID for all combined sessions
  // const combineTeamsByAthleteId =
  //   useTeamsByAthleteId(combineSession?.session?.id) || {};

  // Create a merged object of all teams by athlete ID from all combined sessions
  // const combinedTeamsByAthleteId = combinedSessions.reduce((acc, session) => {
  //   const sessionTeams = useTeamsByAthleteId(session?.id) || {};
  //   return { ...acc, ...sessionTeams };
  // }, {});

  // Collect all teams by athlete ID before reducing
  // const teamsBySession = combinedSessions.map(session => ({
  //   sessionId: session?.id,
  //   teams: useTeamsByAthleteId(session?.id) || {}
  // }));

  // // Merge teams into a single object
  // const combinedTeamsByAthleteId = teamsBySession.reduce((acc, { teams }) => {
  //   return { ...acc, ...teams };
  // }, {});

  // Get teams by athlete ID for the main session
  const mainTeamsByAthleteId = useTeamsByAthleteId(sessionId);

  // Get teams by athlete ID for the legacy combine session
  const combineTeamsByAthleteId = useTeamsByAthleteId(
    combineSession?.session?.id
  );

  // Get combined session IDs
  const combinedSessionIds = useMemo(
    () => combinedSessions.map((session) => session?.id).filter(Boolean),
    [combinedSessions]
  );

  // Use the new hook to get teams for all combined sessions
  const combinedTeamsByAthleteId = useCombinedTeamsByAthleteIds(
    combinedSessionIds
  );

  // Merge all team data
  const teamsByAthleteId = useMemo(
    () => ({
      ...mainTeamsByAthleteId,
      ...combineTeamsByAthleteId,
      ...combinedTeamsByAthleteId,
    }),
    [mainTeamsByAthleteId, combineTeamsByAthleteId, combinedTeamsByAthleteId]
  );

  const sheetOptions = useMemo(
    () =>
      generateSheetOptions(
        teams,
        individuals,
        {
          ...format,
          isMakeMultiDuals: multiDualSheets,
          isLineupVerification: lineupVerification,
          isRotationSummary: rotationSummary,
          isOfficialScoreSheetOnly: officialScoreSheetOnly,
        },
        apparatusArray,
        { singleTeamOfficial }
      ) || [],
    [
      teams,
      individuals,
      format,
      apparatusArray,
      multiDualSheets,
      lineupVerification,
      rotationSummary,
      officialScoreSheetOnly,
      singleTeamOfficial,
    ]
  );

  const deriveSheetTitle = useCallback(
    (selector) => {
      if (!selector) {
        return 'Official Score Sheet';
      }

      const {
        TEAM,
        CHAMPIONSHIPS,
        SUMMARY,
        AA,
        APPARATUS,
        BREAKDOWN,
        LINEUP,
        ROTATION_SUMMARY,
        SINGLE_TEAM,
      } = SCORESHEET_TYPES;

      const formatTeamNames = (indices = []) => {
        const names = indices
          .map((i) => teams?.[i])
          .filter(Boolean)
          .map((team) => team?.displayName || team?.name || '')
          .filter(Boolean);
        return names.length ? names.join(' vs ') : '';
      };

      if (selector.includes(LINEUP)) {
        const index = parseInt(selector.replace(LINEUP, ''), 10);
        const teamName = teams?.[index]?.displayName || teams?.[index]?.name;
        return teamName
          ? `Lineup Verification – ${teamName}`
          : 'Lineup Verification';
      }

      if (selector === ROTATION_SUMMARY) {
        return 'Rotation Summary';
      }

      if (selector === SUMMARY) {
        return 'Team Results Summary';
      }

      if (selector.includes(SINGLE_TEAM)) {
        const teamIndex = parseInt(selector.replace(SINGLE_TEAM, ''), 10);
        const teamName =
          teams?.[teamIndex]?.displayName || teams?.[teamIndex]?.name;
        return teamName
          ? `Official Score Sheet – ${teamName}`
          : 'Official Score Sheet';
      }

      if (selector === AA) {
        return 'All-Around Results';
      }

      if (selector.includes(CHAMPIONSHIPS)) {
        const index = parseInt(selector.replace(CHAMPIONSHIPS, ''), 10);
        const teamName = teams?.[index]?.displayName || teams?.[index]?.name;
        return teamName
          ? `Championships Score Sheet – ${teamName}`
          : 'Championships Score Sheet';
      }

      if (selector.includes(BREAKDOWN)) {
        const indices = selector
          .replace(BREAKDOWN, '')
          .split('_')
          .map((val) => parseInt(val, 10))
          .filter((num) => !Number.isNaN(num));
        const teamNames = formatTeamNames(indices);
        return teamNames ? `Team Breakdown – ${teamNames}` : 'Team Breakdown';
      }

      if (selector.includes(TEAM)) {
        const indices = selector
          .replace(TEAM, '')
          .split('_')
          .map((val) => parseInt(val, 10))
          .filter((num) => !Number.isNaN(num));
        const teamNames = formatTeamNames(indices);
        return teamNames
          ? `Official Score Sheet – ${teamNames}`
          : 'Official Score Sheet';
      }

      const getApparatusName = (abbv) => {
        if (!abbv) {
          return '';
        }
        return (
          abbvToApparatusName?.[abbv] ||
          abbvToApparatusName?.[abbv.toUpperCase?.()] ||
          abbv
        );
      };

      if (apparatusArray.includes(selector)) {
        return `${getApparatusName(selector)} Apparatus`;
      }

      if (selector.includes(APPARATUS)) {
        const apparatus = selector.replace(APPARATUS, '');
        return apparatus
          ? `${getApparatusName(apparatus)} Apparatus`
          : 'Apparatus Score Sheet';
      }

      return 'Official Score Sheet';
    },
    [teams, apparatusArray]
  );

  useEffect(() => {
    if (scoresheetData.location) {
      setLocation(scoresheetData.location);
    }
  }, [scoresheetData.location]);

  useEffect(() => {
    if (scoresheetData.attendance !== undefined) {
      setAttendance(sanitizeAttendanceValue(scoresheetData.attendance));
    } else {
      setAttendance('');
    }
  }, [scoresheetData.attendance]);

  const currentSheetSelector = sheetOptions[sheetIndex] || sheetOptions[0];

  const sheetPageTitle = useMemo(() => deriveSheetTitle(currentSheetSelector), [
    deriveSheetTitle,
    currentSheetSelector,
  ]);

  const baseTitleRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title;
    }
    const baseTitle = sessionName || 'Virtius';
    const newTitle = sheetPageTitle
      ? `${baseTitle} – ${sheetPageTitle}`
      : baseTitle;
    document.title = newTitle;
  }, [sheetPageTitle, sessionName]);

  useEffect(() => {
    return () => {
      if (typeof document === 'undefined') {
        return;
      }
      if (baseTitleRef.current) {
        document.title = baseTitleRef.current;
        baseTitleRef.current = null;
      }
    };
  }, []);

  const {
    main,
    first,
    mainF,
    mainM,
    noPrint,
    sideBySideContainer,
    sideBySidePage,
    sideBySidePlaceholder,
  } = officialscoresheetStyles;

  const genderClass = session.gender === GenderType.FEMALE ? mainF : mainM;

  const judgesByApparatus =
    session.gender === GenderType.FEMALE
      ? {
          [GymApparatus.VT]: { headJudge: null, rest: {} },
          [GymApparatus.UB]: { headJudge: null, rest: {} },
          [GymApparatus.BB]: { headJudge: null, rest: {} },
          [GymApparatus.FX]: { headJudge: null, rest: {} },
        }
      : {
          [GymApparatus.FX]: { headJudge: null, rest: {} },
          [GymApparatus.PH]: { headJudge: null, rest: {} },
          [GymApparatus.SR]: { headJudge: null, rest: {} },
          [GymApparatus.VT]: { headJudge: null, rest: {} },
          [GymApparatus.PB]: { headJudge: null, rest: {} },
          [GymApparatus.HB]: { headJudge: null, rest: {} },
        };

  const isFigPanel = session?.judgePanel?.startsWith('FIG');
  const headJudgeScoreType = isFigPanel
    ? ScoreType.D1
    : session.gender === GenderType.FEMALE
    ? ScoreType.J1
    : ScoreType.D;

  // Judge configuration
  session?.rotations?.items?.forEach((rotation) => {
    if (!rotation._deleted) {
      rotation?.stages?.items?.forEach((stage) => {
        if (!stage._deleted) {
          const judgesForStage = judgesByApparatus[stage.apparatus];
          stage?.judges?.items?.forEach((stageJudge, index) => {
            if (!stageJudge._deleted) {
              const thisJudgeId = stageJudge.sessionJudgeAssignment?.judge?.id;
              if (!thisJudgeId) {
                // Skip judges without valid assignment
                return;
              }
              if (stageJudge.scoreTypes.indexOf(headJudgeScoreType) !== -1) {
                if (!judgesForStage.headJudge) {
                  judgesForStage.headJudge = stageJudge;
                } else if (
                  judgesForStage.headJudge.sessionJudgeAssignment?.judge?.id !==
                    thisJudgeId &&
                  !judgesForStage.rest[thisJudgeId]
                ) {
                  judgesForStage.rest[thisJudgeId] = stageJudge;
                }
              } else if (!judgesForStage.rest[thisJudgeId]) {
                judgesForStage.rest[thisJudgeId] = stageJudge;
              }
            }
          });
        }
      });
    }
  });

  const maxJudges = Object.keys(judgesByApparatus).reduce(
    (acc, apparatus) =>
      Math.max(acc, Object.keys(judgesByApparatus[apparatus].rest).length),
    0
  );

  // All inline scoresheet functions removed - now using imported components from:
  // - ./scoresheet/mens-h2h/MensH2HScoresheet.jsx
  // - ./scoresheet/womens-single/WomensSingleScoresheet.jsx
  // - ./scoresheet/sheets/*.jsx

  function ScoreSheet({ selector, page }) {
    const {
      TEAM,
      CHAMPIONSHIPS,
      SUMMARY,
      AA,
      APPARATUS,
      BREAKDOWN,
      LINEUP,
      ROTATION_SUMMARY,
      SINGLE_TEAM,
    } = SCORESHEET_TYPES;

    const isApparatus = apparatusArray.includes(selector);
    const isTeam = selector.startsWith(TEAM);
    const isChampionships = selector.startsWith(CHAMPIONSHIPS);
    const isBreakdown = selector.startsWith(BREAKDOWN);
    const isLineup = selector.startsWith(LINEUP);
    const isSingleTeam = selector.startsWith(SINGLE_TEAM);

    let options = '';
    let subSelector = selector;

    if (isApparatus) {
      subSelector = APPARATUS;
      options = selector;
    }

    if (isSingleTeam) {
      subSelector = SINGLE_TEAM;
      options = selector.replace(SINGLE_TEAM, '');
    }

    if (isChampionships) {
      subSelector = CHAMPIONSHIPS;
      options = selector.replace(CHAMPIONSHIPS, '');
    }

    if (isTeam) {
      subSelector = TEAM;
      options = selector.replace(TEAM, '');
    }

    if (isBreakdown) {
      subSelector = BREAKDOWN;
      options = selector.replace(BREAKDOWN, '');
    }

    if (isLineup) {
      subSelector = LINEUP;
      options = selector.replace(LINEUP, '');
    }

    switch (subSelector) {
      case TEAM:
        return (
          <MensH2HScoresheet
            options={options}
            page={page}
            teams={teams}
            teamAIndex={teamAIndex}
            teamBIndex={teamBIndex}
            format={format}
            date={date}
            timezone={timezone}
            location={location}
            attendance={attendance}
            session={session}
            apparatusArray={apparatusArray}
            lineupMax={lineupMax}
            teamsByAthleteId={teamsByAthleteId}
            judgesByApparatus={judgesByApparatus}
            maxJudges={maxJudges}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            onEditClick={() => setShowSetup(true)}
            editable={!isPdfMode && judgingEnabled}
            onLocationChange={handleLocationChange}
            onAttendanceChange={handleAttendanceChange}
          />
        );
      case SUMMARY:
        return (
          <TeamSummarySheet
            page={page}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            altTeamScoring={altTeamScoring}
            combineKey={combineKey}
            combineKeys={combineKeys}
            combineSession={combineSession}
            combinedSessions={combinedSessions.filter((s) => s)}
            teams={teams}
            apparatusArray={apparatusArray}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
          />
        );
      case CHAMPIONSHIPS: {
        const teamIndex = options && options.length > 0 ? parseInt(options) : 0;
        const team = teams[teamIndex] || {};
        return (
          <ChampionshipsSheet
            team={team}
            page={page}
            format={format}
            date={date}
            timezone={timezone}
            location={location}
            attendance={attendance}
            session={session}
            apparatusArray={apparatusArray}
            lineupMax={lineupMax}
            judgesByApparatus={judgesByApparatus}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            editable={!isPdfMode}
          />
        );
      }
      case SINGLE_TEAM:
        return (
          <WomensSingleScoresheet
            options={options}
            page={page}
            teams={teams}
            teamAIndex={teamAIndex}
            format={format}
            date={date}
            timezone={timezone}
            location={location}
            attendance={attendance}
            session={session}
            apparatusArray={apparatusArray}
            lineupMax={lineupMax}
            teamsByAthleteId={teamsByAthleteId}
            judgesByApparatus={judgesByApparatus}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            editable={!isPdfMode && judgingEnabled}
            onRefetch={refetch}
            onLocationChange={handleLocationChange}
            onAttendanceChange={handleAttendanceChange}
          />
        );
      case AA:
        return (
          <AASheet
            page={page}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            combineKey={combineKey}
            combineKeys={combineKeys}
            combineSession={combineSession}
            combinedSessions={combinedSessions.filter((s) => s)}
            individuals={individuals?.['AA'] || []}
            apparatusArray={apparatusArray}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
          />
        );
      case APPARATUS: {
        const apparatusCode = options;
        return (
          <ApparatusSheet
            page={page}
            apparatus={apparatusCode}
            abbvToApparatusName={abbvToApparatusName}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            combineKey={combineKey}
            combineKeys={combineKeys}
            combineSession={combineSession}
            combinedSessions={combinedSessions.filter((s) => s)}
            individuals={individuals?.[apparatusCode] || []}
            apparatusArray={apparatusArray}
            teamsByAthleteId={teamsByAthleteId}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
          />
        );
      }
      case BREAKDOWN:
        return (
          <BreakdownSheet
            page={page}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            altTeamScoring={altTeamScoring}
            combineKey={combineKey}
            combineKeys={combineKeys}
            combineSession={combineSession}
            combinedSessions={combinedSessions.filter((s) => s)}
            teams={teams}
            apparatusArray={apparatusArray}
            teamsByAthleteId={teamsByAthleteId}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            teamAIndex={teamAIndex}
            teamBIndex={teamBIndex}
            options={options}
          />
        );
      case LINEUP:
        return (
          <LineupSheet
            page={page}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            combineKey={combineKey}
            combineSession={combineSession}
            teams={teams}
            apparatusArray={apparatusArray}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            options={options}
          />
        );
      case ROTATION_SUMMARY:
        return (
          <RotationSheet
            page={page}
            sessionName={sessionName}
            date={date}
            timezone={timezone}
            location={location}
            combineKey={combineKey}
            combineSession={combineSession}
            teams={rawTeams}
            apparatusArray={apparatusArray}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            sessionRosters={session?.rosters?.items}
          />
        );
      default:
        return (
          <MensH2HScoresheet
            options={options}
            page={page}
            teams={teams}
            teamAIndex={teamAIndex}
            teamBIndex={teamBIndex}
            format={format}
            date={date}
            timezone={timezone}
            location={location}
            attendance={attendance}
            session={session}
            apparatusArray={apparatusArray}
            lineupMax={lineupMax}
            teamsByAthleteId={teamsByAthleteId}
            judgesByApparatus={judgesByApparatus}
            maxJudges={maxJudges}
            singleSheetOnly={singleSheetOnly}
            sheetOptionsLength={sheetOptions.length}
            onEditClick={() => setShowSetup(true)}
            editable={!isPdfMode && judgingEnabled}
            onLocationChange={handleLocationChange}
            onAttendanceChange={handleAttendanceChange}
          />
        );
    }
  }

  // console.log(sheetIndex);
  // console.log(sheetOptions);

  const scoresheetRefs = useState([]);

  function SingleScoreSheet({ sheet, page, currentPage }) {
    const { singleSheetOnly } = useContext(ScoreSheetContext);
    const ref = useRef();
    const { ref: inViewRef, inView } = useInView({
      threshold: 0.25,
      delay: 500,
    });

    const setRefs = useCallback(
      (node) => {
        ref.current = node;
        inViewRef(node);
        scoresheetRefs[page - 1] = ref.current;
      },
      [inViewRef]
    );

    const handleIntersection = (entries) => {
      let mostImportantElement = null;
      let mostImportantOverlap = 0;
      let mostImportantIndex = null;

      entries.forEach((entry, index) => {
        if (!entry?.getBoundingClientRect) {
          return;
        }

        const vpOffset = entry.getBoundingClientRect();
        const vpHeight = window.innerHeight;
        const inView =
          (vpOffset.top > 0 && vpOffset.top < vpHeight) ||
          (vpOffset.bottom < vpHeight && vpOffset.bottom > 0) ||
          (vpOffset.top < 0 && vpOffset.bottom > vpHeight);

        const verticalIntersection = Math.max(
          0,
          Math.min(vpOffset.bottom, window.innerHeight) -
            Math.max(vpOffset.top, 0)
        );
        const verticalOverlapPercentage =
          (verticalIntersection / vpOffset.height) * 100;

        if (inView) {
          if (
            !mostImportantElement ||
            verticalOverlapPercentage > mostImportantOverlap
          ) {
            mostImportantElement = entry;
            mostImportantOverlap = verticalOverlapPercentage;
            mostImportantIndex = index;
          }
        }
      });

      return {
        index: mostImportantIndex,
        element: mostImportantElement,
        overlap: mostImportantOverlap,
      };
    };

    useEffect(() => {
      if (inView && !toggleNextSheet) {
        const { index } = handleIntersection(scoresheetRefs);
        if (index !== null) {
          setSheetIndex(index);
        }
      }
    }, [inView, toggleNextSheet]);

    const showSheet =
      (singleSheetOnly && page === currentPage) || !singleSheetOnly;

    return !showSheet ? null : (
      <div
        ref={setRefs}
        id={`scoresheet_${page}`}
        className={[
          main,
          genderClass,
          'scoresheet',
          page === 1 ? first : null,
        ].join(' ')}
      >
        <ScoreSheet selector={sheet} page={page} />
      </div>
    );
  }

  function ScoreSheetSet() {
    const { sideBySide } = useContext(ScoreSheetContext);
    const containerRef = useRef(null);

    // Calculate and update scale based on viewport width
    useEffect(() => {
      if (!sideBySide || isPdfMode) return;

      const updateScale = () => {
        // 8.5in at 96dpi = 816px per sheet
        const sheetWidth = 816;
        const sheetHeight = 1056; // 11in at 96dpi
        const viewportWidth = window.innerWidth;
        const gap = 16; // Small gap between pages in px
        // Calculate scale to fit two sheets plus gap within viewport
        const twoSheetWidth = sheetWidth * 2 + gap;
        // Use 98% of viewport, cap between 0.4 and 1.0
        const scale = Math.min(
          1.0,
          Math.max(0.4, (viewportWidth * 0.98) / twoSheetWidth)
        );

        if (containerRef.current) {
          containerRef.current.style.setProperty('--sbs-scale', scale);
          // Compensate for scaled height - negative margin pulls up next row
          const marginCompensationV = sheetHeight * (1 - scale);
          containerRef.current.style.setProperty(
            '--sbs-margin-v',
            `-${marginCompensationV}px`
          );
          // Compensate for scaled width - negative margin pulls right page closer
          const marginCompensationH = sheetWidth * (1 - scale);
          containerRef.current.style.setProperty(
            '--sbs-margin-h',
            `-${marginCompensationH}px`
          );
        }
      };

      updateScale();
      window.addEventListener('resize', updateScale);
      return () => window.removeEventListener('resize', updateScale);
    }, [sideBySide]);

    // Refs for side-by-side pair elements
    const pairRefs = useRef([]);

    // Find which pair has the most viewport overlap
    const findMostVisiblePair = useCallback(() => {
      let mostVisibleIndex = null;
      let mostVisibleOverlap = 0;

      pairRefs.current.forEach((el, index) => {
        if (!el) return;

        const rect = el.getBoundingClientRect();
        const vpHeight = window.innerHeight;

        // Check if element is in view at all
        const isInView =
          (rect.top > 0 && rect.top < vpHeight) ||
          (rect.bottom < vpHeight && rect.bottom > 0) ||
          (rect.top < 0 && rect.bottom > vpHeight);

        if (!isInView) return;

        // Calculate vertical overlap
        const intersection = Math.max(
          0,
          Math.min(rect.bottom, vpHeight) - Math.max(rect.top, 0)
        );
        const overlapPct = (intersection / rect.height) * 100;

        if (overlapPct > mostVisibleOverlap) {
          mostVisibleOverlap = overlapPct;
          mostVisibleIndex = index;
        }
      });

      return mostVisibleIndex;
    }, []);

    // Debounced scroll handler for side-by-side mode
    const handleSideBySideScroll = useCallback(
      debounce(() => {
        if (toggleNextSheet) return;

        const mostVisiblePairIndex = findMostVisiblePair();
        if (mostVisiblePairIndex !== null) {
          const newSheetIndex = mostVisiblePairIndex * 2;
          // Only update if actually different to prevent loops
          setSheetIndex((prev) =>
            prev !== newSheetIndex ? newSheetIndex : prev
          );
        }
      }, 150),
      [findMostVisiblePair, toggleNextSheet]
    );

    // Set up scroll listener for side-by-side mode
    useEffect(() => {
      if (!sideBySide || isPdfMode) return;

      window.addEventListener('scroll', handleSideBySideScroll);
      return () => window.removeEventListener('scroll', handleSideBySideScroll);
    }, [sideBySide, isPdfMode, handleSideBySideScroll]);

    // Side-by-side pair component (no individual scroll tracking)
    function SideBySidePair({ pair, pairIndex }) {
      const setPairRef = useCallback(
        (node) => {
          pairRefs.current[pairIndex] = node;
        },
        [pairIndex]
      );

      return (
        <div
          ref={setPairRef}
          key={`pair_${pairIndex}`}
          className={sideBySideContainer}
          id={`scoresheet_pair_${pairIndex}`}
        >
          <div className={sideBySidePage}>
            <div
              id={`scoresheet_${pair.left.page}`}
              className={[main, genderClass, 'scoresheet', first].join(' ')}
            >
              <ScoreSheet selector={pair.left.sheet} page={pair.left.page} />
            </div>
          </div>
          {pair.right ? (
            <div className={sideBySidePage}>
              <div
                id={`scoresheet_${pair.right.page}`}
                className={[main, genderClass, 'scoresheet'].join(' ')}
              >
                <ScoreSheet
                  selector={pair.right.sheet}
                  page={pair.right.page}
                />
              </div>
            </div>
          ) : (
            <div className={sideBySidePlaceholder}>
              <div className={[main, genderClass].join(' ')} />
            </div>
          )}
        </div>
      );
    }

    // Side-by-side mode: render pages in pairs
    if (sideBySide && !isPdfMode) {
      const pairs = [];
      for (let i = 0; i < sheetOptions.length; i += 2) {
        pairs.push({
          left: { sheet: sheetOptions[i], page: i + 1 },
          right:
            i + 1 < sheetOptions.length
              ? { sheet: sheetOptions[i + 1], page: i + 2 }
              : null,
        });
      }

      return (
        <div ref={containerRef}>
          {pairs.map((pair, pairIndex) => (
            <SideBySidePair
              key={`pair_${pairIndex}`}
              pair={pair}
              pairIndex={pairIndex}
            />
          ))}
        </div>
      );
    }

    // Normal mode: render pages one at a time
    return (
      <>
        {sheetOptions.map((sheet, page) => {
          return (
            <SingleScoreSheet
              key={`scoresheet_${page + 1}`}
              sheet={sheet}
              page={page + 1}
              currentPage={sheetIndex + 1}
            />
          );
        })}
      </>
    );
  }

  function ScoreSheetHeader() {
    const { hideJudgeNames, singleTeamOfficial, sideBySide } = useContext(
      ScoreSheetContext
    );

    // In side-by-side mode, progress should be based on the right page of the pair
    // so we can reach 100% at the end
    const progressPct = useMemo(() => {
      if (sideBySide && !isPdfMode) {
        // Use the right page of the current pair (sheetIndex + 2), capped at total
        const rightPagePosition = Math.min(sheetIndex + 2, sheetOptions.length);
        return (rightPagePosition / sheetOptions.length) * 100;
      }
      return ((sheetIndex + 1) / sheetOptions.length) * 100;
    }, [sheetIndex, sheetOptions.length, sideBySide, isPdfMode]);

    return (
      <Header className={noPrint}>
        <MenuBubble />
        <ScoreSheetSetupButton handler={() => setShowSetup(true)} />
        <ScoreSheetPrintButton
          handler={() =>
            exportPDF({
              sessionId: sessionKey,
              sessionName,
              dispatch,
              combineKey,
              combineKeys,
              timezone,
              hideJudgeNames: noJudge || hideJudgeNames,
              lineupVerification: lineupVerification || lineupVerificationMode,
              rotationSummary: rotationSummary || rotationSummaryMode,
              singleSheet: singleSheetOnly || singleSheet,
              singleTeamOfficial,
              officialScoreSheetOnlyMode: officialScoreSheetOnly,
            })
          }
        />
        <ScoreSheetPrevButton disabled={sheetIndex === 0} handler={handleUp} />
        <ScoreSheetNextButton
          disabled={
            sideBySide && !isPdfMode
              ? sheetIndex >= sheetOptions.length - 2 // Last pair starts at length-2 or length-1
              : sheetIndex === sheetOptions.length - 1
          }
          handler={handleDown}
        />
        <ScrollShadow pct={progressPct} />
        <UserBubble />
      </Header>
    );
  }

  return (
    <>
      <ScoreSheetHeader />
      <ScoreSheetSet />
      <ScoreSheetSetup
        show={showSetup}
        setShow={setShowSetup}
        teams={teams}
        teamAIndex={teamAIndex}
        teamBIndex={teamBIndex}
        setTeamAIndex={setTeamAIndex}
        setTeamBIndex={setTeamBIndex}
        location={location}
        attendance={attendance}
        setLocation={setLocation}
        setAttendance={setAttendance}
        sessionId={sessionId}
        sessionVersion={session._version}
        altTeamScoring={altTeamScoring}
        setAltTeamScoring={setAltTeamScoring}
        setCombineKey={setCombineKey}
        setCombineSessions={setCombineSessions}
        combineSessions={combineSessions}
        combineSession={combineSession}
        combineKey={combineKey}
        combineLoaded={combineLoaded}
        combinedSessionsLoaded={combinedSessionsLoaded}
        setCombinedSessionsLoaded={setCombinedSessionsLoaded}
        combineKeys={combineKeys}
        setCombineKeys={setCombineKeys}
        combinedSessions={combinedSessions}
      />
      <Footer hide={showSetup} />
    </>
  );
}
